
import { ExtractionRule } from '../types';

/**
 * Safely accesses a nested property in an object using string path (e.g. "user.address.city")
 */
const getByPath = (obj: any, path: string): any => {
    if (!path || path === '.') return obj;
    return path.split('.').reduce((acc, part) => {
        // Handle arrays like users[0]
        if (part.includes('[') && part.endsWith(']')) {
            const [name, indexStr] = part.split('[');
            const index = parseInt(indexStr.replace(']', ''), 10);
            return acc && acc[name] ? acc[name][index] : undefined;
        }
        return acc && acc[part] !== undefined ? acc[part] : undefined;
    }, obj);
};

const castValue = (value: any, type?: string) => {
    if (value === undefined || value === null) return null;
    
    // If no type specified, infer or pass through (Dynamic)
    if (!type || type === 'auto') return value;

    switch (type) {
        case 'string': return String(value);
        case 'number': return Number(value);
        case 'boolean': return Boolean(value);
        case 'object': return typeof value === 'object' ? value : { value };
        case 'array': return Array.isArray(value) ? value : [value];
        default: return value;
    }
};

/**
 * Heuristic to detect content type for UI display
 */
export const detectContentType = (content: string): 'JSON' | 'XML' | 'Text' => {
    if (!content || typeof content !== 'string') return 'Text';
    const trimmed = content.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) return 'JSON';
    if (trimmed.startsWith('<')) return 'XML';
    return 'Text';
};

export const extractData = (
    content: string, 
    rules: ExtractionRule[]
): Record<string, any> => {
    const results: Record<string, any> = {};

    if (!rules || rules.length === 0) return results;
    if (!content || typeof content !== 'string') return results;

    // Cache parsed versions to avoid re-parsing for every rule
    let cachedJson: any = null;
    let cachedXml: Document | null = null;

    const getJson = () => {
        if (cachedJson) return cachedJson;
        try {
            cachedJson = JSON.parse(content);
            return cachedJson;
        } catch (e) {
            return null;
        }
    };

    const getXml = () => {
        if (cachedXml) return cachedXml;
        try {
            const parser = new DOMParser();
            cachedXml = parser.parseFromString(content, "text/xml");
            return cachedXml;
        } catch (e) {
            return null;
        }
    };

    rules.forEach(rule => {
        try {
            let extractedValue: any = undefined;

            switch (rule.method) {
                case 'jsonpath':
                    const json = getJson();
                    if (json) {
                        extractedValue = getByPath(json, rule.source);
                    }
                    break;

                case 'xpath':
                    const xml = getXml();
                    if (xml) {
                        try {
                            // Simple selector support instead of full XPath for browser compatibility
                            // Real XPath would require document.evaluate
                            const node = xml.querySelector(rule.source);
                            if (node) {
                                extractedValue = node.textContent;
                            }
                        } catch (e) {
                            console.warn(`XML Selector failed: ${rule.source}`);
                        }
                    }
                    break;

                case 'regex':
                    try {
                        const regex = new RegExp(rule.source);
                        const match = content.match(regex);
                        if (match) {
                            // Prefer first capturing group, else full match
                            extractedValue = match[1] !== undefined ? match[1] : match[0];
                        }
                    } catch (e) {
                        console.warn(`Regex failed: ${rule.source}`);
                    }
                    break;
            }

            if (extractedValue !== undefined) {
                results[rule.targetVar] = castValue(extractedValue, rule.type);
            }

        } catch (error) {
            console.error(`Error applying rule ${rule.targetVar}`, error);
        }
    });

    return results;
};